import { assertValue } from '@shared/helpers'
import {
  IfpApplicationRequestData,
  IfpCompanyForm,
  IfpEmploymentType,
  IfpLoan,
  IfpOccupation,
  IfpWorkingHours,
  SecLoanCarOwnershipType,
  SecLoanChildrenAgeGroup,
  SecLoanOtherIncomeType,
  TimeUntilEmployedAgainEstimation,
} from '@shared/sec-loans'
import { YesNo } from '@ui-common/types'
import { resolveYesNoToBooleanInput, toNumber } from '@ui-common/utils'
import { IntlShape } from 'react-intl'

import { isFormDataValid } from '../../../wizard/wizardData'
import { getWizardFlow, omitFinnishPhoneCountryCode } from '../../utils'
import { IfpWizardData } from '../../wizard/types'
import { IfpWizardType } from '../../wizard/wizardMachine'
import { createFormValidator } from './formValidator'
import { ApplicantInfoFormData } from './types'

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getIfpApplicantInfoLocalizedEnums = (formatMessage: IntlShape['formatMessage']) => {
  const yesNoTexts: Record<YesNo, string> = {
    [YesNo.Yes]: formatMessage({ id: 'common-yes' }),
    [YesNo.No]: formatMessage({ id: 'common-no' }),
  }

  const occupationLabels: Record<IfpOccupation, string> = {
    [IfpOccupation.Employed]: formatMessage({ id: 'common-applications-occupation-employed' }),
    [IfpOccupation.Entrepreneur]: formatMessage({ id: 'common-applications-occupation-entrepreneur' }),
    [IfpOccupation.Furlough]: formatMessage({ id: 'ifp-loans-applicant-occupation-furlough' }),
    [IfpOccupation.LongtermSickLeave]: formatMessage({ id: 'ifp-loans-applicant-occupation-longterm-sick-leave' }),
    [IfpOccupation.Pensioner]: formatMessage({ id: 'ifp-loans-applicant-occupation-pensioner' }),
    [IfpOccupation.StudyLeave]: formatMessage({ id: 'ifp-loans-applicant-occupation-study-leave' }),
    [IfpOccupation.Unemployed]: formatMessage({
      id: 'common-applications-occupation-unemployed',
    }),
    [IfpOccupation.Student]: formatMessage({
      id: 'common-applications-occupation-student',
    }),
    [IfpOccupation.OnFamilyLeave]: formatMessage({ id: 'ifp-loans-applicant-occupation-on-family-leave' }),
    [IfpOccupation.Other]: formatMessage({ id: 'ifp-loans-applicant-occupation-other' }),
  }

  const employmentTypeLabels: Record<IfpEmploymentType, string> = {
    [IfpEmploymentType.Permanent]: formatMessage({ id: 'employment-info-employment-type-permanent' }),
    [IfpEmploymentType.TimeLimited]: formatMessage({ id: 'employment-info-employment-type-time-limited' }),
  }

  const workingHoursLabels: Record<IfpWorkingHours, string> = {
    [IfpWorkingHours.FullTime]: formatMessage({ id: 'sec-loans-employment-info-working-hours-form-option-full-time' }),
    [IfpWorkingHours.PartTime]: formatMessage({ id: 'sec-loans-employment-info-working-hours-form-option-part-time' }),
  }

  const companyLegalFormLabels: Record<IfpCompanyForm, string> = {
    [IfpCompanyForm.LimitedCompany]: formatMessage({ id: 'sec-loans-company-form-limited-company' }),
    [IfpCompanyForm.LimitedPartnership]: formatMessage({ id: 'sec-loans-company-form-limited-partnership' }),
    [IfpCompanyForm.OpenCompany]: formatMessage({ id: 'sec-loans-company-form-open-company' }),
    [IfpCompanyForm.TradeName]: formatMessage({ id: 'sec-loans-company-form-trade-name' }),
  }

  const timeUntilEmployedAgainEstimationLabels: Record<TimeUntilEmployedAgainEstimation, string> = {
    [TimeUntilEmployedAgainEstimation.OneToThreeMonths]: '1-3',
    [TimeUntilEmployedAgainEstimation.FourToSixMonths]: '4-6',
    [TimeUntilEmployedAgainEstimation.SevenToTwelveMonths]: '7-12',
    [TimeUntilEmployedAgainEstimation.OverAYear]: formatMessage({
      id: 'ifp-loans-applicant-info-time-until-employed-again-estimation-option-over-a-year',
    }),
  }

  const incomeInfoOtherIncomeLabelText: Record<SecLoanOtherIncomeType, string> = {
    [SecLoanOtherIncomeType.ChildAllowance]: formatMessage({
      id: 'sec-loans-income-info-other-income-form-option-child-allowance',
    }),
    [SecLoanOtherIncomeType.RentalIncome]: formatMessage({
      id: 'sec-loans-income-info-other-income-form-option-rental-income',
    }),
    [SecLoanOtherIncomeType.RegularExpenseReimbursements]: formatMessage({
      id: 'sec-loans-income-info-other-income-form-option-regular-expense-reimbursements',
    }),
    [SecLoanOtherIncomeType.DividendIncome]: formatMessage({
      id: 'sec-loans-income-info-other-income-form-option-dividend-income',
    }),
    [SecLoanOtherIncomeType.SideJob]: formatMessage({ id: 'sec-loans-income-info-other-income-form-option-side-job' }),
    [SecLoanOtherIncomeType.HousingAllowance]: formatMessage({
      id: 'sec-loans-income-info-other-income-form-option-housing-allowance',
    }),
    [SecLoanOtherIncomeType.Grant]: formatMessage({ id: 'sec-loans-income-info-other-income-form-option-grant' }),
    [SecLoanOtherIncomeType.ChildSupport]: formatMessage({
      id: 'sec-loans-income-info-other-income-form-option-child-support',
    }),
    [SecLoanOtherIncomeType.Pension]: formatMessage({ id: 'sec-loans-income-info-other-income-form-option-pension' }),
    [SecLoanOtherIncomeType.Other]: formatMessage({ id: 'common-other' }),
  }

  const expenseInfoCarOwnershipLabelTexts: Record<SecLoanCarOwnershipType, string> = {
    [SecLoanCarOwnershipType.OwnCar]: formatMessage({
      id: 'sec-loans-expenses-info-car-ownership-form-option-own-car',
    }),
    [SecLoanCarOwnershipType.Leasing]: formatMessage({
      id: 'sec-loans-expenses-info-car-ownership-form-option-leasing',
    }),

    [SecLoanCarOwnershipType.CompanyCarBenefitInKind]: formatMessage({
      id: 'sec-loans-expenses-info-car-ownership-form-option-benefit-in-kind',
    }),

    [SecLoanCarOwnershipType.CompanyCarFringeBenefit]: formatMessage({
      id: 'sec-loans-expenses-info-car-ownership-form-option-fringe-benefit',
    }),

    [SecLoanCarOwnershipType.OwnMultipleCars]: formatMessage({
      id: 'sec-loans-expenses-info-car-ownership-form-option-multiple-car',
    }),
  }

  const expenseInfoChildAgeLabelText: Record<SecLoanChildrenAgeGroup, string> = {
    [SecLoanChildrenAgeGroup.ZeroToTen]: SecLoanChildrenAgeGroup.ZeroToTen,
    [SecLoanChildrenAgeGroup.ElevenToFifteen]: SecLoanChildrenAgeGroup.ElevenToFifteen,
    [SecLoanChildrenAgeGroup.SixteenToEighteen]: SecLoanChildrenAgeGroup.SixteenToEighteen,
  }

  return {
    yesNoTexts,
    occupationLabels,
    employmentTypeLabels,
    workingHoursLabels,
    companyLegalFormLabels,
    timeUntilEmployedAgainEstimationLabels,
    incomeInfoOtherIncomeLabelText,
    expenseInfoCarOwnershipLabelTexts,
    expenseInfoChildAgeLabelText,
  }
}

export const wizardDataToFormData = (data: ApplicantInfoFormData): ApplicantInfoFormData => {
  return {
    ...data,
    phoneNumber: omitFinnishPhoneCountryCode(data.phoneNumber),
  }
}

export const isApplicantInfoValid = (applicantInfo: ApplicantInfoFormData, isShortFlow: boolean): boolean => {
  const isValid = isFormDataValid(wizardDataToFormData(applicantInfo), (formatMessage) =>
    createFormValidator({ formatMessage, isShortFlow, isAsyncValidationEnabled: false }),
  )
  return isValid
}

export const getAndValidateApplicantsInfoData = (
  data: IfpWizardData,
  loan: IfpLoan,
): IfpApplicationRequestData['applicantInfos'] | undefined => {
  const basicInfo = data.basicInfo
  const applicantInfos = data.applicantInfos
  const wizardType = getWizardFlow({
    durationInMonths: toNumber(basicInfo?.durationInMonths),
    hasFinancialDifficulties: basicInfo?.hasFinancialDifficulties,
    principalExemptionMonthsLeft: loan.principalExemptionMonthsLeft,
  })
  const isLongFlow = wizardType === IfpWizardType.Long

  return applicantInfos?.map((applicantInfo) => {
    const isShortFlow = wizardType === IfpWizardType.Short
    const isValid = isApplicantInfoValid(applicantInfo, isShortFlow)
    return {
      uuid: applicantInfo.uuid,
      phoneNumber: assertValue(applicantInfo.phoneNumber, 'applicantInfo.phoneNumber'),
      ...(isLongFlow && isValid
        ? {
            occupation: applicantInfo.occupation,
            workingHours: applicantInfo.workingHours,
            legalFormOfTheCompany: applicantInfo.legalFormOfTheCompany,
            employer: applicantInfo.employer,
            businessId: applicantInfo.businessId,
            carOwnershipType: applicantInfo.carOwnershipType,
            companyFoundingDate: applicantInfo.companyFoundingDate,
            companyName: applicantInfo.companyName,
            employmentEndDate: applicantInfo.employmentEndDate,
            employmentStartDate: applicantInfo.employmentStartDate,
            employmentType: applicantInfo.employmentType,
            estimatedFurloughEndDate: applicantInfo.estimatedFurloughEndDate,
            estimatedReturnToWorkDate: applicantInfo.estimatedReturnToWorkDate,
            profession: applicantInfo.profession,
            timeUntilEmployedAgainEstimation: applicantInfo.timeUntilEmployedAgainEstimation,
            hasJobToReturnTo: resolveYesNoToBooleanInput(applicantInfo.hasJobToReturnTo),
            expectsToBeEmployedAgain: resolveYesNoToBooleanInput(applicantInfo.expectsToBeEmployedAgain),
            otherIncomes: applicantInfo.hasOtherIncome
              ? (applicantInfo.otherIncomes?.map((otherIncome) => ({
                  incomeType: assertValue(otherIncome.incomeType, 'otherIncome.incomeType'),
                  amountAfterTaxes: assertValue(toNumber(otherIncome.amountAfterTaxes), 'otherIncome.amountAfterTaxes'),
                })) ?? [])
              : undefined,
            totalSavings: toNumber(applicantInfo.totalSavings),
            ownShareOfHousingExpenses: toNumber(applicantInfo.ownShareOfHousingExpenses),
            childSupports: applicantInfo.isPayingChildSupport
              ? (applicantInfo.childSupports?.map((childSupport) => ({
                  supportAmount: assertValue(toNumber(childSupport.supportAmount), 'childSupport.supportAmount'),
                  ageGroup: assertValue(childSupport.ageGroup, 'childSupport.ageGroup'),
                })) ?? [])
              : undefined,
            currentIncomeBeforeTaxes: toNumber(applicantInfo.currentIncomeBeforeTaxes),
            currentIncomeAfterTaxes: toNumber(applicantInfo.currentIncomeAfterTaxes),
            normalGrossSalary: toNumber(applicantInfo.normalGrossSalary),
            normalNetSalary: toNumber(applicantInfo.normalNetSalary),
            incomeBeforeTaxes: toNumber(applicantInfo.incomeBeforeTaxes),
            incomeAfterTaxes: toNumber(applicantInfo.incomeAfterTaxes),
            grossSalary: toNumber(applicantInfo.grossSalary),
            netSalary: toNumber(applicantInfo.netSalary),
            totalLoansAmount: toNumber(applicantInfo.totalLoansAmount),
            totalAmountOfLoanRepaymentsPerMonth: toNumber(applicantInfo.totalAmountOfLoanRepaymentsPerMonth),
            totalHousingCompanyLoan: toNumber(applicantInfo.totalHousingCompanyLoan),
          }
        : undefined),
    }
  })
}
